import React, { useState, useEffect } from 'react';
import './SearchModal.css';
import { getToken } from '../../../../../services/api';
import { taxaSelicService } from '../../../../../services/authService';
import { MdClose, MdSearch, MdAdd, MdPerson, MdDescription, MdCalendarToday, MdWarning } from 'react-icons/md';
import { FaUser, FaBuilding } from 'react-icons/fa';

const SearchModal = ({ isOpen, onClose, state, onPrecatorioAdded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const [taxasData, setTaxasData] = useState([]);

  // Fetch taxas when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTaxas();
    }
  }, [isOpen]);

  const fetchTaxas = async () => {
    try {
      const response = await taxaSelicService.getAll();
      if (response.success) {
        setTaxasData(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching taxas:', error);
    }
  };

  if (!isOpen) return null;

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Por favor, digite algo para pesquisar');
      return;
    }

    if (searchQuery.trim().length < 3) {
      setError('Digite pelo menos 3 caracteres para pesquisar');
      return;
    }

    setSearching(true);
    setError(null);

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/precatorios/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: searchQuery.trim(),
          state: state
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar precatórios');
      }

      const data = await response.json();
      setSearchResults(data.results || []);
      
      if (data.results.length === 0) {
        setError('Nenhum precatório encontrado');
      }
    } catch (err) {
      setError('Erro ao buscar precatórios. Tente novamente.');
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddPrecatorio = async (precatorio) => {
    setAddingId(precatorio.numero_precatorio);
    setError(null);

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/contacted-precatorios/${state}/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          numero_precatorio: precatorio.numero_precatorio,
          document_used: precatorio.documentNumber?.replace(/\D/g, '') || '',
          document_type_used: precatorio.documentType || 'CPF',
          observacoes: ''
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao adicionar precatório');
      }

      // Remove from search results
      setSearchResults(prev => prev.filter(p => p.numero_precatorio !== precatorio.numero_precatorio));
      
      // Show success message
      alert('Precatório adicionado com sucesso!');
      
      // Notify parent component
      if (onPrecatorioAdded) {
        onPrecatorioAdded(data.data);
      }
    } catch (err) {
      setError(err.message || 'Erro ao adicionar precatório');
      console.error('Add error:', err);
    } finally {
      setAddingId(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calculateUpdatedValue = (precatorio) => {
    // Check if all required fields exist
    if (!precatorio.orcamento || !precatorio.ano_pagamento_previsto || !precatorio.valor_atualizado) {
      return null;
    }

    // Parse years
    const startYear = parseInt(precatorio.orcamento);
    const endYear = parseInt(precatorio.ano_pagamento_previsto);

    // Validate years
    if (isNaN(startYear) || isNaN(endYear) || startYear >= endYear) {
      return null;
    }

    // Start with valor_atualizado
    let valor = precatorio.valor_atualizado;

    // Apply compound interest year by year
    for (let year = startYear; year <= endYear; year++) {
      const taxaEntry = taxasData.find(t => t.ano === year);
      if (!taxaEntry) {
        // Missing rate for this year, don't show calculated value
        return null;
      }
      // Apply compound interest: valor * (1 + taxa)
      valor = valor * (1 + taxaEntry.taxa);
    }

    return valor;
  };

  const calculateCompoundInterestFactor = (anoPagamentoPrevisto) => {
    if (!anoPagamentoPrevisto) return null;

    const currentYear = new Date().getFullYear();
    const endYear = parseInt(anoPagamentoPrevisto);

    if (isNaN(endYear) || currentYear > endYear) {
      return null;
    }

    const years = endYear - currentYear;
    const fixedRate = 0.06; // 6% per year
    const factor = Math.pow(1 + fixedRate, years);

    return factor;
  };

  const formatNatureza = (natureza) => {
    if (!natureza) return '-';
    
    const naturezaMap = {
      'C': 'Comum',
      'A': 'Alimentar',
      'P': 'Prioritário'
    };
    
    return naturezaMap[natureza.toUpperCase()] || natureza;
  };

  const getDocumentIcon = (type) => {
    if (type === 'CPF') return <FaUser className="doc-icon cpf" />;
    if (type === 'CNPJ') return <FaBuilding className="doc-icon cnpj" />;
    return <MdDescription className="doc-icon" />;
  };

  return (
    <div className="search-modal-overlay" onClick={onClose}>
      <div className="search-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Pesquisar Precatórios - {state}</h2>
          <button className="close-button" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="modal-body">
          <div className="search-section">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Digite CPF, CNPJ, número de precatório ou nome..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input"
                autoFocus
              />
              <button 
                onClick={handleSearch} 
                className="search-btn"
                disabled={searching}
              >
                {searching ? 'Pesquisando...' : 'Pesquisar'}
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>

          <div className="results-section">
            {searchResults.length > 0 && (
              <>
                <div className="results-header">
                  <h3>Resultados ({searchResults.length})</h3>
                </div>
                <div className="results-list">
                  {searchResults.map((result) => (
                    <div key={result.numero_precatorio} className="result-item">
                      <div className="result-main">
                        <div className="result-identifier">
                          {getDocumentIcon(result.documentType)}
                          <div className="identifier-info">
                            <span className="doc-type">{result.documentType || 'Documento'}</span>
                            <span className="doc-number">{result.documentNumber || '-'}</span>
                          </div>
                        </div>

                        <div className="result-details">
                          <div className="detail-row">
                            <MdPerson className="detail-icon" />
                            <span className="detail-label">Nome:</span>
                            <span className="detail-value">{result.nome || 'Não informado'}</span>
                          </div>
                          <div className="detail-row">
                            <MdDescription className="detail-icon" />
                            <span className="detail-label">Precatório:</span>
                            <span className="detail-value precatorio-num">{result.numero_precatorio}</span>
                          </div>
                          {result.natureza && (
                            <div className="detail-row">
                              <span className="detail-label">Natureza:</span>
                              <span className="detail-value">{formatNatureza(result.natureza)}</span>
                            </div>
                          )}
                          {result.orcamento && (
                            <div className="detail-row">
                              <MdCalendarToday className="detail-icon" />
                              <span className="detail-label">Orçamento:</span>
                              <span className="detail-value">{result.orcamento}</span>
                            </div>
                          )}
                          {result.ano_pagamento_previsto && (
                            <div className="detail-row">
                              <MdCalendarToday className="detail-icon" />
                              <span className="detail-label">Ano Previsto:</span>
                              <span className="detail-value">{result.ano_pagamento_previsto}</span>
                            </div>
                          )}
                
                        </div>

                        <div className="result-value">
                          <span className="value-label">Valor Atualizado</span>
                          <span className="value-amount">{formatCurrency(result.valor_atualizado)}</span>

                          {(() => {
                            const calculatedValue = calculateUpdatedValue(result);
                            return calculatedValue ? (
                              <>
                                <span className="value-label" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                                  Valor Atualizado e Calculado
                                </span>
                                <span className="value-amount" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                                  {formatCurrency(calculatedValue)}
                                </span>
                              </>
                            ) : null;
                          })()}

                          {(() => {
                            const naturezaUpper = result.natureza?.toUpperCase();
                            const naturezaLower = result.natureza?.toLowerCase();
                            const isIRRFApplicable = naturezaUpper === 'C' || naturezaLower === 'comum' ||
                                                     naturezaUpper === 'A' || naturezaLower === 'alimentar';

                            if (!isIRRFApplicable) {
                              return null;
                            }

                            const calculatedValue = calculateUpdatedValue(result);
                            const baseValue = calculatedValue || result.valor_atualizado;

                            if (!baseValue) return null;

                            return (
                              <>
                                <span className="value-label" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                                  Valor Líquido (27,5 % - IRRF)
                                </span>
                                <span className="value-amount" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                                  {formatCurrency(baseValue * 0.725)}
                                </span>
                              </>
                            );
                          })()}

                          {(() => {
                            const naturezaUpper = result.natureza?.toUpperCase();
                            const naturezaLower = result.natureza?.toLowerCase();
                            const isIRRFApplicable = naturezaUpper === 'C' || naturezaLower === 'comum' ||
                                                     naturezaUpper === 'A' || naturezaLower === 'alimentar';

                            if (!isIRRFApplicable) {
                              return null;
                            }

                            const calculatedValue = calculateUpdatedValue(result);
                            const baseValue = calculatedValue || result.valor_atualizado;
                            const valorLiquido = baseValue * 0.725;

                            const compoundFactor = calculateCompoundInterestFactor(result.ano_pagamento_previsto);
                            if (!compoundFactor) return null;

                            const valorCompraMaximo = valorLiquido / (2 * compoundFactor);
                            const percentual = (valorCompraMaximo / valorLiquido) * 100;

                            return (
                              <>
                                <span className="value-label" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                                  Valor de Compra Máximo
                                </span>
                                <span className="value-amount" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                                  {formatCurrency(valorCompraMaximo)} ({percentual.toFixed(1)}%)
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="result-actions">
                        {result.owner ? (
                          <div className="owner-warning">
                            <MdWarning /> Já está com: <strong>{result.owner.name}</strong>
                          </div>
                        ) : (
                          <button 
                            className="add-button"
                            onClick={() => handleAddPrecatorio(result)}
                            disabled={addingId === result.numero_precatorio}
                          >
                            {addingId === result.numero_precatorio ? (
                              'Adicionando...'
                            ) : (
                              <>
                                <MdAdd /> Adicionar
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {searching && (
              <div className="searching-state">
                <div className="spinner"></div>
                <p>Pesquisando precatórios...</p>
              </div>
            )}

            {!searching && searchResults.length === 0 && !error && (
              <div className="empty-state">
                <MdSearch className="empty-icon" />
                <p>Pesquise por CPF, CNPJ, número de precatório ou nome</p>
                <small>Os resultados aparecerão aqui</small>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;